
import React from 'react';
import { Name } from "./name";
import { questionFontSize, TEXT_FONT_SIZE} from "../flow-render";
import { deep_copy } from "../js-utils";
import { get_bounding_div_bbox } from "./bbox_utils";
import faker from 'faker';
import "./round-checkbox.scss";
import { Popup } from '../onboarding-shared/popup';

// faker constants
const RADIO_SINGLE_RANDOM_VALUE = "RADIO_SINGLE_RANDOM_VALUE";
const RADIO_SET_FIRST_VALUE = "RADIO_SET_FIRST_VALUE";

class RadioButton {

  static type = "RadioButton";

  /**
   * @param name {Name} instance
   * @param buttonList {[Name]}
   * @param initValues {[String]} Any values listed here will be turned on.
   * @param fake {obj} path to fake function.
   */
  constructor(name, buttonList, initValues = [], fake=null) {
    this.name = name;
    this.initValues = initValues;
    this.fake = fake;

    this.setButtonList(buttonList);

    this.doRadio = true;
    this.type = RadioButton.type;
    this.do_checkbox = false;
    this.round_checkbox = false;
  };

  setCallback = cb => {
    this.onChangeCallback = cb;
  };

  setButtonList = buttonList => {
    if (!buttonList) {
      buttonList = [];
    }
    this.refs = [];
    // If we don't prepend the name.id to the parameter, multiple
    // yes/no buttons will have name collisions.
    this.buttonList = buttonList.map((prm, index) => {
      this.refs.push(React.createRef());
      return new Name(this.name.id + "|" + prm.id, prm.label, index, prm.value);
    });
  }

  /**
   * Initialize the component state. Usually called from
   * the component constructor.
   */
  initState = state => {
    this.buttonList.forEach(prm => {
      const id_suffix = prm.id.split("|")[1];
      if (!state[prm.id]) {
        state[prm.id] = {
          value: this.initValues.includes(id_suffix),
          disable: false,
          type: RadioButton.type,
          show: true,
        }
      }
    })
  };

  dumpState = state => {
    Object.keys(state).forEach(prop => {
      if (prop.startsWith(this.name.id)) {
        console.log(prop, "=", state[prop])
      }
    })
  }

  /**
   * @returns the id of the currently selected button. This assumes that only
   * one button is selected as it returns the first one selected.
   */
  getValue = (state, stripPrefix = false) => {
    let result = null;
    for(let i=0; i < this.buttonList.length; i++) {
      let button = this.buttonList[i];
      if (state[button.id].value) {
        result = button.id;
      }
    }

    if (result && stripPrefix) {
      result = result.split('|')[1];
    }
    return result;
  };

  setValue = (state, index, value) => {
    state[this.buttonList[index].id].value = value;
  }

  setValues = (state, ids, value) => {
    this.buttonList.forEach(button => {
      const suffix = button.id.split("|")[1];
      if (ids.includes(suffix)) {
        state[button.id].value = value;
      }
    })
  }

  getSelectedValues = state => {
    const values = [];
    this.buttonList.forEach(button => {
      if (state[button.id].value) {
        values.push(button.id.split("|")[1]);
      }
    });
    return values;
  }

  getValues = (stripPrefix=false) => {
    return this.buttonList.map(button => {
      if (stripPrefix) {
        return button.id.split("|")[1];
      }
      return button.id;
    });
  };

  getDisabled = (state, prm) => {
    if (!prm) {
      return this.getAllDisabled(state);
    }
    return state[prm.id].disable;
  };

  getAllDisabled = state => {
    for(let i=0; i < this.buttonList.length; i++) {
      if (!(state[this.buttonList[i].id].disable)) {
        return false;
      }
    }
    return true;
  };

  isSelected = (state, index) => {
    return state[this.buttonList[index].id].value;
  };

  /**
   * Sets the display {@param state} for the label at
   * {@param index} to {@param show}.
   */
  setDisplay = (state, index, show) => {
    state[this.buttonList[index].id].show = show;
  };

  // Return an array of selected values
  getAppValues = (appState, stripPrefix = false) => {
    let result = appState[this.name.id];

    if (!result) {
      result = [];
    }
    if (stripPrefix) {
      result = result.map(id => id.split('|')[1]);
    }
    return result;
  };

  // Return the first selected label
  getAppButtons = (appState) => {
    const values = this.getAppValues(appState);
    const buttons = [];
    values.forEach(value => {
      const button = this.buttonList.find(button => button.id === value);
      if (button) {
        buttons.push(button);
      }
    });
    return buttons;
  };

  /**
   * @Return the button containing {@param value}.
   */
  getButton = value => {
    return this.buttonList.find(button => button.id === value);
  }

  loadFromState = (appState, componentState) => {
    if (appState && !appState[this.name.id]) {
      return;
    }
    if (appState) {
      const values = appState[this.name.id];
      this.buttonList.forEach(prm => {
        if (!componentState[prm.id]) {
          componentState[prm.id] = {
            value: false,
            disable: false,
            type: RadioButton.type,
            show: true,
          }
        }
        componentState[prm.id].value = values.includes(prm.id);
      })
    }
  };

  getName = index => {
    return this.buttonList[index];
  };

  random = faker => {
    if (this.buttonList.length === 1) {
      if (faker.random.number({min: 0, max: 1, precision: 1 })) {
        return this.buttonList[0];
      }
      return null;
    }
    return faker.random.arrayElement(this.buttonList);
  };

  clear = state => {
    this.buttonList.forEach(button => {
      state[button.id].value = false;
    });
  };

  setRandomSingleValue = (state) => {
    this.clear(state);

    if (this.buttonList.length === 1) {
      if (faker.random.number({min: 0, max: 1, precision: 1 })) {
        state[this.buttonList[0].id].value = true;
      }
    } else {
      const button = faker.random.arrayElement(this.buttonList);
      state[button.id].value = true;
    }
  }

  getSelectedId = state => {
    for(let i=0; i < this.buttonList.length; i++) {
      if (state[this.buttonList[i].id].value) {
        return this.buttonList[i].id;
      }
    }
    return null;
  };

  saveToState = state => {
    const selected_buttons = this.buttonList.filter(
      button => state[button.id].value);

    return {
      [this.name.id]: selected_buttons.map(button => button.id),
    }
  };

  containsId = id => {
    return this.buttonList.find(prm => prm.id === id);
  };

  /**
   * Enforce the radio button behavior for a click at {@param id}.
   */
  processClick = (id, state) => {
    if (!this.containsId(id)) {
      return;
    }

    if (this.doRadio) {
      this.buttonList.forEach(prm => {
        state[prm.id].value = prm.id === id ? !(state[prm.id].value) : false;
      });
    } else {
      state[id].value = !(state[id].value);
    }
  };

  /**
   * Returns {@code true} if any of the items are clicked.
   */
  isClicked = state => {
    return this.buttonList.filter(
      item => state[item.id] && state[item.id].value).length > 0;
  };

  isItemDefined = (index, state) => {
    return this.buttonList[index].id in state;
  }

  isItemClicked = (index, state) => {
    return state[this.buttonList[index].id].value;
  };

  renderLabel = (index, state, style_override={}, props={}) => {
    let name = this.buttonList[index];
    let style = {
      color: state[name.id].disable ? "#AAA" : "#000",
      fontSize: "1rem",
      display: "block",
      marginLeft: "2rem",
      ...style_override,
    };
    return (
      <span {...props} style={style}>{name.label}</span>
    );
  };

  renderCheckboxDiv = (index, component, style, elemPrefix, elemPostfix) => {
    const div_style = {
      display: "inline-block",
    }
    return (
      <div style={div_style}>
        { elemPrefix }
        { this.renderCheckbox(index, component, style) }
        { elemPostfix }
      </div>
    )
  };

  renderCheckbox = (index, component, style = {}) => {
    let checkboxStyle = {
      float: "left",
      transform: "scale(1.2)",
      margin: "0px",
      ...style,

    };
    let id = this.buttonList[index].id;
    let elem_id = id;
    if (elem_id.includes("|")) {
      elem_id = elem_id.split("|").pop();
    }
    const input_type = this.do_checkbox ? "checkbox" : "radio";
    const args = {};
    if (this.round_checkbox) {
      args.className = "checkbox-round";
    }
    return (
        <input
            type={input_type}
            id={elem_id}
            style={checkboxStyle}
            { ...args}
            value={component.state[id].value}
            ref={this.refs[index]}
            onChange={this.onChange(component, id)}
            disabled={component.state[id].disable}
            checked={component.state[id].value}
        />
    );
  };

  renderRadioLabel = (index, component, style = {}, labelStyle = {}) => {
    let checkboxStyle = {
      float: "left",
      transform: "scale(1.2)",
      margin: "0px",
      ...style,

    };
    let id = this.buttonList[index].id;
    let label = this.buttonList[index].label;
    let elem_id = id;
    if (elem_id.includes("|")) {
      elem_id = elem_id.split("|").pop();
    }
    const input_type = this.do_checkbox ? "checkbox" : "radio";
    const args = {};
    if (this.round_checkbox) {
      args.className = "checkbox-round";
    }
    return (
        <label style={{ display: 'flex', gap: '16px', cursor: 'pointer', ...labelStyle }}>
            <input
                type={input_type}
                id={elem_id}
                style={checkboxStyle}
                { ...args}
                value={component.state[id].value}
                ref={this.refs[index]}
                onChange={this.onChange(component, id)}
                disabled={component.state[id].disable}
                checked={component.state[id].value}
            />
            <div>
                {label}
            </div>
        </label>
      
    );
  };

  renderRadioLabelWithInfo = (index, component, info, style = {}, labelStyle = {}) => {
    let checkboxStyle = {
      float: "left",
      transform: "scale(1.2)",
      margin: "0px",
      ...style,

    };
    let id = this.buttonList[index].id;
    let label = this.buttonList[index].label;
    let elem_id = id;
    if (elem_id.includes("|")) {
      elem_id = elem_id.split("|").pop();
    }
    const input_type = this.do_checkbox ? "checkbox" : "radio";
    const args = {};
    if (this.round_checkbox) {
      args.className = "checkbox-round";
    }
    return (
        <label style={{ display: 'flex', gap: '16px', cursor: 'pointer', ...labelStyle }}>
            <input
                type={input_type}
                id={elem_id}
                style={checkboxStyle}
                { ...args}
                value={component.state[id].value}
                ref={this.refs[index]}
                onChange={this.onChange(component, id)}
                disabled={component.state[id].disable}
                checked={component.state[id].value}
            />
            <div>
                {label}
                {info}
            </div>
        </label>
      
    );
  };

  

  renderRadioLabelInfo = (index, component, rollOverInfo, marginInfo, style = {}) => {
    let checkboxStyle = {
      float: "left",
      transform: "scale(1.2)",
      margin: "0px",
      ...style,

    };
    let id = this.buttonList[index].id;
    let label = this.buttonList[index].label;
    let elem_id = id;
    if (elem_id.includes("|")) {
      elem_id = elem_id.split("|").pop();
    }
    const input_type = this.do_checkbox ? "checkbox" : "radio";
    const args = {};
    if (this.round_checkbox) {
      args.className = "checkbox-round";
    }

    const handleOnMouseEnter = (isEnter) => {
        component.setState({
            rollover_text: isEnter ? index : -1
        })
    }

    const handleOnMouseLeave = (isEnter) => {
        component.setState({
            rollover_text: isEnter ? index : -1
        })
    }

    return (
        <label style={{ display: 'flex', gap: '16px', cursor: 'pointer' }}>
            <input
                type={input_type}
                id={elem_id}
                style={checkboxStyle}
                { ...args}
                value={component.state[id].value}
                ref={this.refs[index]}
                onChange={this.onChange(component, id)}
                disabled={component.state[id].disable}
                checked={component.state[id].value}
            />

            <div 
                style={{ border: '1px solid black', padding: '0.5rem 1rem', width: '25rem', color: 'black' }} 
                onMouseEnter={() => handleOnMouseEnter(true)}
                onMouseLeave={() => handleOnMouseLeave(false)}
            >
                {label}
            </div>

            {component.state.rollover_text === index ? 
                (<Popup style={{ border: '1px solid black', padding: '0.5rem 1rem', width: '30rem', color: 'black', transform: 'translate(25rem, -1rem)', pointerEvents: 'none' }}>
                    <span>{rollOverInfo}</span>
                </Popup>) : null
            }

            <div style={{ display: 'flex', alignItems: 'center' }}>{marginInfo}</div>
        </label>
    );
  };

    renderCheckboxWithInfo = (index, component, children, style = {}) => {
        let checkboxStyle = {
            float: "left",
            transform: "scale(1.2)",
            margin: "0px",
            ...style,

        };
        let id = this.buttonList[index].id;
        let elem_id = id;
        if (elem_id.includes("|")) {
            elem_id = elem_id.split("|").pop();
        }
        const input_type = this.do_checkbox ? "checkbox" : "radio";

        const args = {};
        if (this.round_checkbox) {
            args.className = "checkbox-round";
        }
        return (
            <label style={{ display: 'flex', gap: '16px', alignItems: 'center', cursor: 'pointer' }}>
                <input
                    type={input_type}
                    id={elem_id}
                    style={checkboxStyle}
                    { ...args}
                    value={component.state[id].value}
                    ref={this.refs[index]}
                    onChange={this.onChange(component, id)}
                    disabled={component.state[id].disable}
                    checked={component.state[id].value}
                />
                {children}
            </label>
        );  
    };



  renderCheckboxLabel = (
    index, component, check_box_style = {}, label_style = {}, suffix_elem, div_style = {}) => {
    let name = this.buttonList[index];
    return (
      <p key={name.id} style={div_style}>
        { this.renderCheckbox(index, component, check_box_style) }
        { this.renderLabel(index, component.state, label_style)}
        { suffix_elem }
      </p>
    );
  };
  
  renderCheckboxLabel = (
    index, component, check_box_style = {}, label_style = {}, suffix_elem, div_style = {}) => {
    let name = this.buttonList[index];
    return (
      <p key={name.id} style={div_style}>
        { this.renderCheckbox(index, component, check_box_style) }
        { this.renderLabel(index, component.state, label_style)}
        { suffix_elem }
      </p>
    );
  };

  renderCheckboxLabels = (component) => {
    let items = [];
    for (let i=0; i < this.buttonList.length; i++) {
      items.push(this.renderCheckboxLabel(i, component));
    }
    return items;
  };

  /**
   * Using a table as a layout, render the label, then a column of
   * checkboxes on the left, with a column of labels on the right.
   */
  renderTable = (component, labelCallback=null)=> {
    let tdSpace = {
      paddingLeft: "1rem",
    };
    let textStyle = {
      ...tdSpace,
      fontSize: TEXT_FONT_SIZE,
    };
    let checkboxStyle = {
      marginTop: "0.8rem",
    };

    let items = this.buttonList.filter(
      item => component.state[item.id].show);

    items = items.map((item) => {
      let index = item.index;
      return (
        <tr key={item.id}>
          <td> { this.renderCheckbox(index, component, checkboxStyle)} </td>
          <td style={textStyle}> {
            labelCallback
              ? labelCallback(index)
              : this.renderLabel(index, component.state) }</td>
        </tr>
      );
    });

    let labelStyle = {
      fontSize: questionFontSize,
      ...tdSpace,
      paddingBottom: "1rem",
    };
    return (
      <table>
        <tbody>
          <tr><td></td><td style={labelStyle}>{ this.name.label }</td></tr>
          { items }
        </tbody>
      </table>
    );
  };

  onChange = (component, id) => event => {
    let newState = deep_copy(component.state);

    this.processClick(id, newState);
    newState.dirty = true;
    newState.highlight_rects = [];

    if (this.onChangeCallback) {
      this.onChangeCallback(component, newState, this);
    }
    component.setState(newState);
  };

  /**
   * Render the radio boxes in a single row.
   */
  render = (component, checkboxStyle = {}) => {
    let boxes = [];
    let inputStyle = {
      marginLeft: "1rem",
      marginRight: "0.5rem",
      transform: "scale(1.5)",
    };
    let spanStyle = {
      ...checkboxStyle,
      marginRight: "1rem",
      fontSize: TEXT_FONT_SIZE,
    };
    this.buttonList.forEach((id, index) => {
      let currentSpanStyle = Object.assign({}, spanStyle);
      if (this.buttonList[index].style) {
        currentSpanStyle = {
          ...currentSpanStyle,
          ...(this.buttonList[index].style),
        };
      }
      boxes.push( (
        <span key={id.id} style={currentSpanStyle}>
          { this.renderCheckbox(index, component, inputStyle)}
          { id.label }
        </span>
      ))
    });
    if (this.name.label) {
      return (
        <div className="columns">
          <div className="column">
            {this.name.label}: {boxes}
          </div>
        </div>
      );
    } else {
      return boxes;
    }
  };

  setItemDisabled = (index, state, disable) => {
    state[this.buttonList[index].id].disable = disable;
  }

  setEnabled = (items, state) => {
    for(let i=0; i < this.buttonList.length; i++) {
      state[this.buttonList[i].id].disable = !(items.includes(i));
    }
  };

  setDisabled = (state, disabled) => {
    for(let i=0; i < this.buttonList.length; i++) {
      state[this.buttonList[i].id].disable = disabled;
    }
  };

  /**
   * Returns true if the parameter is unset.
   *
   * @param state {Object}
   * @return {boolean}
   */
  isBlank = state => {
    return !this.getValue(state);
  };

  /**
   * If the parameter is set, return true else add the highlight
   * rect.
   *
   * @param state {Object}
   * @param highlight_rects {[Object]} rects
   * @param scale {Object} optional rect size multiplier
   */
  isFilledElseHighlight = (state, highlight_rects, scale={ x: 1.1, y: 1.1}) => {
    const blank = this.isBlank(state);
    if (blank) {
      highlight_rects.push(get_bounding_div_bbox(this.refs, scale));
    }

    return !blank;
  }

  highlightButton = (state, index, highlight_rects, scale={ x: 1.1, y: 1.1} ) => {
    if (this.refs[index].current) {
      highlight_rects.push(get_bounding_div_bbox([this.refs[index]], scale));
    }
  }
}

export { RadioButton, RADIO_SINGLE_RANDOM_VALUE, RADIO_SET_FIRST_VALUE };
